<template>
    <b-modal
        ref="dialog"
        title-tag="h2"
        title="Send SMS"
        :lazy="true"
        @hidden="close"
        no-close-on-backdrop
        no-close-on-esc
    >
        <b-form-group label-cols="3" label="To:" label-for="to">
            <vet-select
                module="client-contact"
                placeholder="Name (required)"
                v-model="recipient"
                field="sms"
                show-menu
                hide-backdrop
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Message:" label-for="message">
            <editable
                v-model="message"
                placeholder="Message (required)"
                :defaultHeight="82"
                :maxlength="maxCharacters"
            />
            <p class="remaining-chars mb-0">
                {{ remainingChars }} character(s) remaining
            </p>
            <p
                class="remaining-chars my-0 cursor-pointer text-primary"
                v-if="smsCount && smsCount > 1"
                @click="isShowSMSExplanation = !isShowSMSExplanation"
            >
                {{ smsCount }} sms combined into single message
            </p>
        </b-form-group>

        <b-alert :show="isShowSMSExplanation && smsCount > 1" variant="warning">
            <i class="cus-error" /> Messages longer than 150 characters will be
            billed as multiple SMS messages but delivered to customer as one
            message.
        </b-alert>

        <template v-slot:modal-footer>
            <loader v-if="isSending" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSending"
            >
                Cancel
            </b-button>

            <b-button v-if="!isSending" variant="primary" @click="send">
                Send
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import Loader from "@/module/commons/loader";
import VetSelect from "@/module/commons/vet-select";
import axios from "axios";
import Editable from "@/components/elements/editable-textarea";
import COMMUNICATION from "@/constants/communications";

export default {
    components: {
        VetSelect,
        Loader,
        Editable
    },
    props: {
        patientDetails: {
            type: Object
        }
    },
    data() {
        return {
            communication: null, // if not null, then this is resend
            recipient: null,
            isSending: false,
            message: "",
            isLoadingList: true,
            clientId: null,
            maxCharacters: 1600,
            originalPhoneNumber: "",
            isShowSMSExplanation: false
        };
    },
    computed: {
        remainingChars() {
            return this.maxCharacters - this.message.length;
        },
        businessName() {
            return this.$store.state.setting.business.name;
        },
        smsCount() {
            if (!this._isExtensionEnabled("SMS")) {
                return null;
            }
            return this.message.length > 0
                ? Math.ceil(this.message.length / 150)
                : 1;
        }
    },
    methods: {
        onOpen() {
            this.setDefaultRecipient();
            this.message = "Msg from " + this.businessName + ".";
        },
        setDefaultRecipient() {
            const owners = this.patientDetails.primary_owner;
            const owner = owners ? owners[0] : null;

            if (owner && owner.mobile && owner.mobile_country_code) {
                this.recipient = owner;
            }
        },
        open(communication) {
            this.communication = communication || null;
            this.$refs.dialog.show();
            this.setDefaultRecipient();
            if (this.communication) {
                this.message = communication.content;
            } else {
                this.message = "Msg from " + this.businessName + ".";
            }
        },
        close() {
            this.$refs.dialog.hide();
        },
        async send() {
            try {
                if (!this.recipient) {
                    throw new Error("Recipient is required");
                }

                if (this.message === "") {
                    throw new Error("Message is required");
                }

                if (!this.recipient.mobile || this.recipient.mobile === "") {
                    throw new Error("Recipient has no mobile number.");
                }

                if (
                    !this.recipient.mobile_country_code ||
                    this.recipient.mobile_country_code === ""
                ) {
                    throw new Error(
                        "Recipient has invalid mobile number (country code)."
                    );
                }

                const { mobile, mobile_country_code } = this.recipient;
                let recipientNumber = `+${mobile_country_code}${mobile}`;

                this.isSending = true;

                let referenceType = 15;
                let referenceId = this.patientDetails.id;
                if (this.communication) {
                    // For Resend SMS
                    // set the reference type & reference id
                    referenceType =
                        COMMUNICATION.REFERENCE_TYPE["resent_communication"];
                    referenceId = this.communication.id;
                }

                const res = await axios.post(
                    `${process.env.BARRAMUNDI}communication/sms-message`,
                    {
                        to: recipientNumber,
                        message: this.message,
                        type: 0,
                        ref_id: this.patientDetails.id,
                        business_id: this._setting.business_id,
                        communication_data: {
                            date: Math.floor(new Date().getTime() / 1000),
                            type: 6,
                            content: this.message,
                            patient_id: this.patientDetails.id,
                            client_id: this.recipient.id,
                            reference_id: referenceId,
                            reference_type: referenceType,
                            business_id: this._setting.business_id,
                            is_system_generated: false
                            // reminder_actor_id: "?"
                        }
                    }
                );

                if (
                    res.data &&
                    res.data.response &&
                    res.data.response.message &&
                    res.data.response.message.code === 200
                ) {
                    this._message("SMS message successfully sent.");
                } else {
                    throw new Error("Error: Unable to send SMS.");
                }

                this.isSending = false;
                this.close();
                this.$emit("onsuccess");
            } catch (e) {
                this.isSending = false;
                this._message(e.message, "danger");
            }
        }
    }
};
</script>

<style scoped>
.remaining-chars {
    color: grey;
    margin-top: 5px;
    font-size: 10px;
}
</style>
